import * as Yup from "yup";
import { catalogName } from "../../../../Utils/catalogName";

// ---------------------------------------------------------------------------
// COMY1-292 — Edit Order editor: local form model, seeding, payload building
// and validation.
//
// The read-only job payload exposes services under `requested_services` and any
// products already on a service order under `products` (falls back to
// `ordered_products`). Field names carry sensible fallbacks so the editor
// degrades gracefully if the backend uses a slightly different key.
// ---------------------------------------------------------------------------

let uidCounter = 0;
export const nextUid = () => {
  uidCounter += 1;
  return `line-${Date.now()}-${uidCounter}`;
};

export const LINE_KINDS = { SERVICE: "service", PRODUCT: "product" };
export const NEW_PRODUCT = "NEW_PRODUCT";

export const emptyVariant = () => ({
  uid: nextUid(),
  variant_id: "",
  variant_label: "",
  amount: 1,
  unit_id: "",
  unit_label: "",
  is_not_listed: false,
  unit_price: "",
});

export const emptyServiceLine = () => ({
  uid: nextUid(),
  kind: LINE_KINDS.SERVICE,
  service_id: "",
  service_label: "",
  variants: [emptyVariant()],
});

export const emptyProductLine = () => ({
  uid: nextUid(),
  kind: LINE_KINDS.PRODUCT,
  product_id: "",
  is_new: false,
  product_name: "",
  product_price: "",
  category_id: "",
  quantity: 1,
  unit_price: "",
});

// Seed the editor from the existing order so the supplier edits what's there.
// The order carries RAW (untranslated) service/variant/unit names; `tr` holds
// the i18next translators ({ service, sku, unit }) so the seeded labels are
// already localized on first paint (the option lists load later in the
// background). Falls back to identity if no translators are provided.
// Agent-created services/variants have no services/sku JSON key — `tr.language`
// lets their own `name_he` show in the Hebrew UI (see catalogName).
export const seedLinesFromJob = (jobData, tr) => {
  const identity = (v, opts) => opts?.defaultValue ?? v;
  const trService = tr?.service || identity;
  const trSku = tr?.sku || identity;
  const trUnit = tr?.unit || ((v) => v);
  const language = tr?.language;
  const lines = [];

  (jobData?.requested_services || []).forEach((service) => {
    const variants = (service.variants || []).map((v) => {
      const unitPrice = v.unit_price ?? v.price_per_unit ?? v.price ?? "";
      const variantKey = v.name || v.sku || "";
      const unitKey = v.unit || "";
      return {
        uid: nextUid(),
        variant_id: v.variant_id ?? v.id ?? "",
        variant_label: variantKey
          ? catalogName(trSku, variantKey, { ns: "sku", nameHe: v.name_he, language })
          : "",
        amount: v.amount ?? v.quantity ?? 1,
        unit_id: v.unit_id ?? "",
        unit_label: unitKey ? trUnit(unitKey, unitKey) : "",
        is_not_listed: !!v.is_not_listed,
        unit_price: unitPrice === null ? "" : unitPrice,
      };
    });
    const serviceKey = service.name || "";
    lines.push({
      uid: nextUid(),
      kind: LINE_KINDS.SERVICE,
      service_id: service.service_id ?? service.id ?? "",
      service_label: serviceKey
        ? catalogName(trService, serviceKey, {
            ns: "services",
            nameHe: service.name_he,
            language,
          })
        : "",
      variants: variants.length ? variants : [emptyVariant()],
    });
  });

  const existingProducts = jobData?.products || jobData?.ordered_products || [];
  existingProducts.forEach((p) => {
    const price = p.unit_price ?? p.list_price ?? p.price ?? "";
    // Ad-hoc order-line products were created hidden (is_hidden, COMY1-292):
    // they aren't in the supplier's catalog, so re-seed them as fully editable
    // ad-hoc lines (name/price/category) instead of locked catalog products.
    // Saving re-creates them as fresh hidden products.
    const isAdHoc = p.is_hidden === true;
    lines.push({
      uid: nextUid(),
      kind: LINE_KINDS.PRODUCT,
      product_id: isAdHoc ? "" : (p.product_id ?? p.id ?? ""),
      is_new: isAdHoc,
      product_name: p.name || "",
      product_price: price === null ? "" : price,
      category_id: p.category_id ?? "",
      quantity: p.quantity ?? 1,
      unit_price: price === null ? "" : price,
    });
  });

  return lines.length ? lines : [emptyServiceLine()];
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Build the exact API contract body for PUT .../jobs/{jobId}/update.
export const buildUpdatePayload = (lines) => {
  const services = [];
  const products = [];

  lines.forEach((line) => {
    if (line.kind === LINE_KINDS.SERVICE) {
      if (!line.service_id) return;
      const variants = (line.variants || [])
        .filter((v) => v.variant_id !== "" && v.variant_id != null)
        .map((v) => ({
          variant_id: v.variant_id,
          amount: num(v.amount),
          unit_id: v.unit_id === "" ? null : v.unit_id,
          unit_price: num(v.unit_price),
        }));
      if (variants.length) services.push({ service_id: line.service_id, variants });
    } else if (line.kind === LINE_KINDS.PRODUCT) {
      if (line.is_new) {
        if (!line.product_name) return;
        products.push({
          new_product: {
            name: line.product_name,
            price: num(line.product_price),
            category_id: line.category_id === "" ? null : line.category_id,
          },
          quantity: num(line.quantity),
          unit_price: num(line.unit_price || line.product_price),
        });
      } else {
        if (!line.product_id) return;
        products.push({
          product_id: line.product_id,
          quantity: num(line.quantity),
          unit_price: num(line.unit_price || line.product_price),
        });
      }
    }
  });

  return { services, products };
};

// Yup schema (used by Formik). Heterogeneous line items are validated with a
// per-item lazy schema keyed off `kind`. A service/product order may be
// services-only, products-only, or a mix — but never empty. A VISIT-ONLY order
// (`allowEmpty`) may be emptied entirely, since it's paid via the visit itself.
// COMY1-292.
export const buildValidationSchema = (t, { allowEmpty = false } = {}) => {
  const serviceLine = Yup.object({
    kind: Yup.string(),
    service_id: Yup.mixed().test(
      "service-required",
      t("edit.errors.service_required"),
      (v) => v !== "" && v != null
    ),
    variants: Yup.array()
      .of(
        Yup.object({
          variant_id: Yup.mixed().test(
            "variant-required",
            t("edit.errors.variant_required"),
            (v) => v !== "" && v != null
          ),
          amount: Yup.number()
            .typeError(t("edit.errors.amount_required"))
            .required(t("edit.errors.amount_required"))
            .moreThan(0, t("edit.errors.amount_required")),
          // A price must be entered, but 0 is a valid price.
          unit_price: Yup.number()
            .typeError(t("edit.errors.price_required"))
            .required(t("edit.errors.price_required"))
            .min(0, t("edit.errors.price_required")),
        })
      )
      .min(1, t("edit.errors.variant_required")),
  });

  const productLine = Yup.object({
    kind: Yup.string(),
    is_new: Yup.boolean(),
    product_id: Yup.mixed().when("is_new", {
      is: false,
      then: (s) =>
        s.test(
          "product-required",
          t("edit.errors.product_required"),
          (v) => v !== "" && v != null
        ),
      otherwise: (s) => s.nullable(),
    }),
    product_name: Yup.string().when("is_new", {
      is: true,
      then: (s) => s.trim().required(t("edit.errors.product_name_required")),
      otherwise: (s) => s.nullable(),
    }),
    // A price must be entered, but 0 is a valid price.
    product_price: Yup.mixed().when("is_new", {
      is: true,
      then: () =>
        Yup.number()
          .typeError(t("edit.errors.price_required"))
          .required(t("edit.errors.price_required"))
          .min(0, t("edit.errors.price_required")),
      otherwise: (s) => s.nullable(),
    }),
    category_id: Yup.mixed().when("is_new", {
      is: true,
      then: (s) =>
        s.test(
          "category-required",
          t("edit.errors.category_required"),
          (v) => v !== "" && v != null
        ),
      otherwise: (s) => s.nullable(),
    }),
    quantity: Yup.number()
      .typeError(t("edit.errors.amount_required"))
      .moreThan(0, t("edit.errors.amount_required")),
  });

  const linesArray = Yup.array().of(
    Yup.lazy((item) =>
      item?.kind === LINE_KINDS.PRODUCT ? productLine : serviceLine
    )
  );

  return Yup.object({
    lines: allowEmpty
      ? linesArray
      : linesArray.min(1, t("edit.errors.at_least_one_line")),
  });
};
